
import React, { useState, useMemo } from 'react';
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { useLanguage } from '@/contexts/LanguageContext';
import { User, Search } from 'lucide-react';

interface GuardSelectProps {
  guards: any[];
  selectedGuardId?: string;
  onSelect: (guardId: string) => void;
}

const GuardSelect: React.FC<GuardSelectProps> = ({
  guards,
  selectedGuardId,
  onSelect
}) => {
  const { t } = useLanguage();
  const [searchTerm, setSearchTerm] = useState('');
  const [isOpen, setIsOpen] = useState(false);

  const filteredGuards = useMemo(() => {
    if (!searchTerm) return guards;

    return guards?.filter(guard => {
      const name = (guard.full_name || `${guard.last_name || ''} ${guard.first_name || ''}`).toLowerCase().trim();
      const searchLower = searchTerm.toLowerCase();
      return name.includes(searchLower);
    }) || [];
  }, [guards, searchTerm]);

  const handleOpenChange = (open: boolean) => {
    setIsOpen(open);
    if (!open) {
      setSearchTerm('');
    }
  };

  return (
    <Select
      value={selectedGuardId ?? 'all'}
      onValueChange={onSelect}
      onOpenChange={handleOpenChange}
    >
      <SelectTrigger className="w-[150px] sm:w-[200px] h-8 text-xs sm:text-sm">
        <User className="h-3.5 w-3.5 mr-2" />
        <SelectValue placeholder={t('selectGuard') || 'Select Guard'} />
      </SelectTrigger>
      <SelectContent className="bg-background border border-border shadow-lg z-50">
        {/* Search Input */}
        <div className="flex items-center px-3 py-2 border-b border-border">
          <Search className="h-4 w-4 mr-2 text-muted-foreground" />
          <Input
            placeholder={t('searchGuards') || 'Search guards...'}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="h-8 border-0 bg-transparent focus-visible:ring-0 focus-visible:ring-offset-0"
            onClick={(e) => e.stopPropagation()}
          />
        </div>
        
        {/* All Guards Option */}
        <SelectItem value="all" className="focus:bg-accent focus:text-accent-foreground">
          {t('allGuards') || 'All Guards'}
        </SelectItem>
        
        {/* Filtered Guards */}
        {filteredGuards?.length > 0 ? (
          filteredGuards.map((guard) => (
            <SelectItem 
              key={guard.id} 
              value={guard.id}
              className="focus:bg-accent focus:text-accent-foreground"
            >
              {guard.full_name || `${guard.last_name || ''} ${guard.first_name || ''}`}
            </SelectItem>
          ))
        ) : searchTerm ? (
          <div className="px-3 py-2 text-sm text-muted-foreground">
            {t('noGuardsFound') || 'No guards found'}
          </div>
        ) : null}
      </SelectContent>
    </Select>
  );
};

export default GuardSelect;
